import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17107 — what a visit debrief created or changed, so its one Undo can put
 * everything back: the records it made (people, opportunities, issues, tasks,
 * competitor links, a planned next visit), the links it added to the visit,
 * and the earlier state of what it changed (the visit itself, an
 * opportunity marked lost, a quote marked lost).
 *
 * Kept on the server rather than handed to the browser, so an Undo can only
 * remove what that debrief made. The person who saved it may undo it once,
 * within a day.
 */
export interface VisitDebriefUndoModel {
	/** The debrief logged a new visit (deleted on Undo); false when it filled in an existing one. */
	visitCreated: boolean;
	/** The trip stop the debrief completed; Undo reopens it (which deletes the visit). */
	salesTrip: { tripId: number; stopId: number } | null;
	/** An existing visit's fields before the debrief, restored on Undo. */
	visitBefore: {
		status: string;
		visitType: string | null;
		subject: string | null;
		objective: string | null;
		keyTakeaways: string | null;
		nextSteps: string | null;
		outcome: string | null;
		clientContactId: number | null;
		customFields: Record<string, unknown> | null;
		isFlagged: boolean;
	} | null;
	/** Visit links the debrief added (an existing visit keeps the rest). */
	visitLinkIds: Array<number>;
	/** Contact records (client_contact) created for new people. */
	contactRecordIds: Array<number>;
	/** People (contact) the debrief created, removed with their customer link. */
	personIds: Array<number>;
	opportunityIds: Array<number>;
	/** Opportunities marked lost: their stage and outcome before. */
	opportunitiesBefore: Array<{
		id: number;
		stageId: number;
		lostReasonId: number | null;
		lostReasonNote: string | null;
		priceGapAmount: string | null;
		priceGapPercent: string | null;
		winnerCompetitorId: number | null;
	}>;
	/** Quotes marked lost: their outcome columns before, restored exactly. */
	quotesBefore: Array<{
		id: number;
		quoteOutcome: string | null;
		quoteLostReasonId: number | null;
		quoteLostNote: string | null;
		quotePriceGapPercent: string | null;
		quoteOutcomeDate: string | null;
	}>;
	/** Customer-competitor links (client_wholesaler) the debrief created. */
	competitorLinkIds: Array<number>;
	/** PD-17109: competitors the debrief put on an opportunity (absent on older debriefs). */
	opportunityCompetitorLinks?: Array<{ opportunityId: number; linkId: number }>;
	/** PD-17109: competitors already on an opportunity whose note or price the debrief changed, as they were. */
	opportunityCompetitorsBefore?: Array<{ opportunityId: number; linkId: number; note: string | null; theirPrice: number | null }>;
	issueIds: Array<number>;
	/** Notes the debrief added to an existing visit (a new visit's note goes with the visit). */
	noteIds: Array<number>;
	/** Tasks by occurrence id, as the tasks API deletes them. */
	taskIds: Array<number>;
	/** The planned next visit it created. */
	nextVisitId: number | null;
}

@Index('visit_debrief__visit', ['fkVisitId'], {})
@Entity('visit_debrief')
export class VisitDebriefEntity extends BaseEntity {
	@Column('int', { name: 'fk_m_sm_client_visit__id', unsigned: true })
	public fkVisitId!: number;

	@Column('json', { name: 'undoRecord', nullable: true })
	public undo!: VisitDebriefUndoModel | null;

	/** Tenant user ids the debrief was shared with (notified in the app and by email). */
	@Column('json', { name: 'sharedUserIds', nullable: true })
	public sharedUserIds!: Array<number> | null;

	@Column('datetime', { name: 'undoneDate', nullable: true })
	public undoneDate!: Date | null;
}
